import { CatState, Direction, Position, TilePos, TileType } from '../types';
import { MAZE_WIDTH, MAZE_HEIGHT, CAT_EXIT } from '../maps/levels';
import { isWalkableForCat } from '../maps/tileTypes';

export interface CatConfig {
  name: string;
  color: string;
  startTile: TilePos;
  scatterTarget: TilePos;
}

export class Cat {
  name: string;
  color: string;
  pos: Position;
  tilePos: TilePos;
  startTile: TilePos;
  scatterTarget: TilePos;
  direction: Direction = Direction.Up;
  state: CatState = CatState.InHouse;
  speed: number;
  scaredSpeed: number;
  animFrame: number = 0;
  animTimer: number = 0;
  scaredTimer: number = 0;
  exitTimer: number = 0;
  exitDelay: number;

  private tileSize: number;

  constructor(config: CatConfig, tileSize: number, speed: number, scaredSpeed: number, exitDelay: number) {
    this.name = config.name;
    this.color = config.color;
    this.startTile = { ...config.startTile };
    this.scatterTarget = { ...config.scatterTarget };
    this.tileSize = tileSize;
    this.speed = speed;
    this.scaredSpeed = scaredSpeed;
    this.exitDelay = exitDelay;
    this.tilePos = { ...config.startTile };
    this.pos = {
      x: config.startTile.col * tileSize + tileSize / 2,
      y: config.startTile.row * tileSize + tileSize / 2,
    };
  }

  reset(): void {
    this.tilePos = { ...this.startTile };
    this.pos = {
      x: this.startTile.col * this.tileSize + this.tileSize / 2,
      y: this.startTile.row * this.tileSize + this.tileSize / 2,
    };
    this.direction = Direction.Up;
    this.state = CatState.InHouse;
    this.scaredTimer = 0;
    this.exitTimer = 0;
    this.animFrame = 0;
  }

  update(dt: number, maze: number[][], targetTile: TilePos): void {
    this.animTimer += dt;
    if (this.animTimer > 0.1) {
      this.animTimer = 0;
      this.animFrame = (this.animFrame + 1) % 4;
    }

    if (this.state === CatState.Scared) {
      this.scaredTimer -= dt;
      if (this.scaredTimer <= 0) {
        this.state = CatState.Hunt;
        this.scaredTimer = 0;
      }
    }

    if (this.state === CatState.InHouse) {
      this.exitTimer += dt;
      if (this.exitTimer >= this.exitDelay) {
        this.state = CatState.Exiting;
      }
      return;
    }

    if (this.state === CatState.Exiting) {
      const exitX = CAT_EXIT.col * this.tileSize + this.tileSize / 2;
      const exitY = CAT_EXIT.row * this.tileSize + this.tileSize / 2;
      const dx = exitX - this.pos.x;
      const dy = exitY - this.pos.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 2) {
        this.pos.x = exitX;
        this.pos.y = exitY;
        this.tilePos = { ...CAT_EXIT };
        this.state = CatState.Hunt;
        this.direction = Direction.Left;
      } else {
        const spd = this.speed * dt;
        this.pos.x += (dx / dist) * spd;
        this.pos.y += (dy / dist) * spd;
      }
      return;
    }

    if (this.state === CatState.Fleeing) {
      const houseX = this.startTile.col * this.tileSize + this.tileSize / 2;
      const houseY = this.startTile.row * this.tileSize + this.tileSize / 2;
      const dx = houseX - this.pos.x;
      const dy = houseY - this.pos.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 2) {
        this.pos.x = houseX;
        this.pos.y = houseY;
        this.tilePos = { ...this.startTile };
        this.state = CatState.InHouse;
        this.exitTimer = this.exitDelay * 0.5;
      } else {
        const spd = this.speed * 1.5 * dt;
        this.pos.x += (dx / dist) * spd;
        this.pos.y += (dy / dist) * spd;
        this.tilePos = {
          col: Math.max(0, Math.min(MAZE_WIDTH - 1, Math.floor(this.pos.x / this.tileSize))),
          row: Math.max(0, Math.min(MAZE_HEIGHT - 1, Math.floor(this.pos.y / this.tileSize))),
        };
      }
      return;
    }

    const currentSpeed = this.state === CatState.Scared ? this.scaredSpeed : this.speed;
    const moveAmt = currentSpeed * dt;

    const centerX = this.tilePos.col * this.tileSize + this.tileSize / 2;
    const centerY = this.tilePos.row * this.tileSize + this.tileSize / 2;
    const threshold = Math.max(moveAmt, 0.5);
    const atCenter = Math.abs(this.pos.x - centerX) < threshold && Math.abs(this.pos.y - centerY) < threshold;

    if (atCenter) {
      this.pos.x = centerX;
      this.pos.y = centerY;

      const bestDir = this.chooseBestDirection(maze, targetTile);
      this.direction = bestDir;
    }

    const { dx, dy } = this.dirDelta(this.direction);
    this.pos.x += dx * moveAmt;
    this.pos.y += dy * moveAmt;

    // Tunnel wrap
    if (this.pos.x < -this.tileSize / 2) {
      this.pos.x = MAZE_WIDTH * this.tileSize - this.tileSize / 2;
    } else if (this.pos.x > MAZE_WIDTH * this.tileSize - this.tileSize / 2) {
      this.pos.x = -this.tileSize / 2;
    }

    this.tilePos = {
      col: Math.max(0, Math.min(MAZE_WIDTH - 1, Math.floor(this.pos.x / this.tileSize))),
      row: Math.max(0, Math.min(MAZE_HEIGHT - 1, Math.floor(this.pos.y / this.tileSize))),
    };
  }

  scare(duration: number): void {
    if (this.state === CatState.Hunt || this.state === CatState.Patrol) {
      this.state = CatState.Scared;
      this.scaredTimer = duration;
      this.direction = this.reverseDirection(this.direction);
    }
  }

  pounce(): void {
    this.state = CatState.Fleeing;
    this.scaredTimer = 0;
  }

  private chooseBestDirection(maze: number[][], target: TilePos): Direction {
    const dirs = [Direction.Up, Direction.Left, Direction.Down, Direction.Right];
    const reverse = this.reverseDirection(this.direction);
    let bestDist = Infinity;
    let bestDir = this.direction;

    for (const d of dirs) {
      if (d === reverse) continue;
      const { dx, dy } = this.dirDelta(d);
      let nc = this.tilePos.col + dx;
      let nr = this.tilePos.row + dy;

      if (nc < 0) nc = MAZE_WIDTH - 1;
      if (nc >= MAZE_WIDTH) nc = 0;
      if (nr < 0 || nr >= MAZE_HEIGHT) continue;

      const tile = maze[nr][nc] as TileType;
      if (!isWalkableForCat(tile)) continue;

      const dist = (nc - target.col) ** 2 + (nr - target.row) ** 2;
      if (dist < bestDist) {
        bestDist = dist;
        bestDir = d;
      }
    }

    return bestDir;
  }

  private reverseDirection(dir: Direction): Direction {
    switch (dir) {
      case Direction.Up: return Direction.Down;
      case Direction.Down: return Direction.Up;
      case Direction.Left: return Direction.Right;
      case Direction.Right: return Direction.Left;
      default: return dir;
    }
  }

  private dirDelta(dir: Direction): { dx: number; dy: number } {
    switch (dir) {
      case Direction.Up: return { dx: 0, dy: -1 };
      case Direction.Down: return { dx: 0, dy: 1 };
      case Direction.Left: return { dx: -1, dy: 0 };
      case Direction.Right: return { dx: 1, dy: 0 };
      default: return { dx: 0, dy: 0 };
    }
  }
}
